/**
 * Billing Service
 * Handles API calls for billing, subscriptions, and invoice management
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface PlanInfo {
  name: string;
  price: number;
  billingCycle: 'monthly' | 'annually';
  renewalDate: string;
}

export interface UsageMetrics {
  seats: {
    used: number;
    total: number;
  };
  records: {
    used: number;
    total: number;
  };
  automationCredits: {
    used: number;
    total: number;
  };
}

export interface BillingAddress {
  email: string;
  companyName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  einNumber?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'card';
  last4: string;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  isPrimary: boolean;
}

export interface Invoice {
  id: string;
  reference: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  date: string;
  downloadUrl?: string;
}

export interface BillingData {
  plan: PlanInfo;
  usage: UsageMetrics;
  billingAddress: BillingAddress;
  paymentMethods: PaymentMethod[];
  invoices: Invoice[];
}

import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

async function getAuthToken(
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> {
  try {
    return await getAccessTokenSilently();
  } catch (error) {
    console.warn('Silent token retrieval failed, trying with popup:', error);
    const token = await getAccessTokenWithPopup();
    if (!token) {
      throw new Error('Failed to get access token');
    }
    return token;
  }
}

/**
 * Get billing data for a company
 */
export const getBillingData = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<BillingData> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/company/${companyId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch billing data: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching billing data:', error);
    throw error;
  }
};

/**
 * Update billing address
 */
export const updateBillingAddress = async (
  companyId: string,
  userId: string,
  address: BillingAddress,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message?: string }> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/company/${companyId}/address`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(address)
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update billing address: ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating billing address:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Download an invoice PDF
 */
export const downloadInvoice = async (
  invoiceId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/invoices/${invoiceId}/download`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to download invoice: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading invoice:', error);
    return false;
  }
};

/**
 * Add a payment method
 */
export const addPaymentMethod = async (
  companyId: string,
  userId: string,
  paymentMethodData: any,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message?: string }> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/company/${companyId}/payment-methods`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentMethodData)
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to add payment method: ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error adding payment method:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Set primary payment method
 */
export const setPrimaryPaymentMethod = async (
  companyId: string,
  userId: string,
  paymentMethodId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message?: string }> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/company/${companyId}/payment-methods/${paymentMethodId}/set-primary`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to set primary payment method: ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error setting primary payment method:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Delete a payment method
 */
export const deletePaymentMethod = async (
  companyId: string,
  userId: string,
  paymentMethodId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message?: string }> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/billing/company/${companyId}/payment-methods/${paymentMethodId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete payment method: ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting payment method:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

